<template>
    <v-layout row>
        <v-flex xs12 sm12>
            <v-card class="elevation-1 transparent">
                <v-toolbar class="transparent" flat height="37">
                    <v-btn dark icon title="Back to Settings" @click="$router.push('/settings')">
                        <v-icon>arrow_back</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>
                    <help-link appId="settings" pageId="workshifts" color="white"></help-link>
                    <v-btn v-if="hasAddRights" fab dark small color="primary" title="Add Work Shift" @click="addWorkshift">
                        <v-icon dark>add</v-icon>
                    </v-btn>
                </v-toolbar>
            </v-card>
            <v-alert v-if="error" :value="true" color="error" icon="error" outline>
                Error encountered. Please contact support. Error details: {{errorMessage}}
            </v-alert>  
            <v-card class="elevation-1 mt-5" > 
                <v-toolbar flat height="45px">
                    <div :class="isMobile?'wp-70 mb-10':'wp-30 mb-10'">
                        <v-text-field v-model="search" append-icon="search" label="Search name or clock start/end" single-line hide-details clearable></v-text-field>
                    </div>
                    <v-spacer></v-spacer>
                </v-toolbar>
                <v-data-table
                    :headers="isMobile ? [] : headers"
                    :items="schedules_filtered"
                    :loading="loading"
                    :rows-per-page-items="rowsPerPageItems"
                    :pagination.sync="paginationSync"
                    :hide-headers="isMobile"
                >
                    <v-progress-linear v-slot:progress color="primary" indeterminate></v-progress-linear>
                    <template v-slot:items="props">

                        <!-- mobile view -->
                        <tr v-if="isMobile">
                            <td :colspan="headers.length" class="pa-1">
                                <v-card class="elevation-1" @click="showAddEdit(props.item)">
                                    <v-toolbar flat height="42">
                                        <v-chip class="user-chip-container" :style="`background-color:${props.item.color?props.item.color:'transparent'};`">
                                            {{ props.item.name }}
                                        </v-chip>
                                        <v-spacer></v-spacer>
                                    </v-toolbar>
                                    <v-layout row wrap align-center class="section-layout">
                                        <v-flex xs12 class="text-xs-left mt-5">
                                            <v-layout row>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Clock-Start</div>
                                                    <div>{{ props.item.clockStart | moment("h:mm a") }}</div>
                                                </v-flex>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Clock-End</div>
                                                    <div>{{ props.item.clockEnd | moment("h:mm a")}}</div>
                                                </v-flex>
                                                 <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Type</div>
                                                    <div v-if="props.item.strict" class="text-xs-left">Strict</div>
                                                    <div v-if="!props.item.strict" class="text-xs-left">Flexible</div>
                                                </v-flex>
                                            </v-layout>
                                            <v-layout row>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Validate Undertime</div>
                                                    <span v-if="props.item.validateUndertime">Yes</span>
                                                    <span v-else>No</span>
                                                </v-flex>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Grace Period</div>
                                                    <div v-if="props.item.strict" class="text-xs-left">{{props.item.gracePeriod}} minutes</div>
                                                    <div v-if="!props.item.strict" class="text-xs-left">N/A</div>
                                                </v-flex>
                                                 <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Total Assigned</div>
                                                    <div>{{ props.item.totalAssigned }}</div>
                                                </v-flex>
                                            </v-layout>
                                        </v-flex>
                                    </v-layout>
                                </v-card>
                            </td>
                        </tr>

                        <!-- desktop view -->
                        <tr v-else @click="showAddEdit(props.item)" title="Click to Edit" class="clickable">
                            <td>
                                <v-chip class="user-chip-container" :style="`background-color:${props.item.color?props.item.color:'transparent'};`">
                                    {{ props.item.name }}
                                </v-chip>
                            </td>
                            <td>{{ props.item.clockStart | moment("h:mm a") }}</td>
                            <td>{{ props.item.clockEnd | moment("h:mm a")}}</td>
                            <td>
                                <div v-if="props.item.strict" class="text-xs-left">Strict</div>
                                <div v-if="!props.item.strict" class="text-xs-left">Flexible</div>
                            </td>
                            <td>
                                <span v-if="props.item.validateUndertime">Yes</span>
                                <span v-else>No</span>
                            </td>
                            <td>
                                <div v-if="props.item.strict" class="text-xs-left">{{props.item.gracePeriod}} minutes</div>
                                <div v-if="!props.item.strict" class="text-xs-left">N/A</div>
                            </td>
                            <td>{{ props.item.totalAssigned }}</td>
                        </tr>
                    </template>
                </v-data-table> 
            </v-card>

            <!-- Comments and Updates -->
            <v-layout row wrap>
                <v-flex xs12 sm6>
                    <!-- comments history component -->
                    <comments-history v-if="!loading" :comments_data="commentsData"></comments-history>
                </v-flex>
                <v-flex xs12 sm6>
                    <update-history v-if="!loading" ref="updateHistoryRef" :page_query_id="pageQueryId"></update-history>
                </v-flex>
            </v-layout>
        </v-flex>   

        <v-dialog v-model="dialogAdd" persistent width="600">
            <add-edit-workshift ref="addEditWorkshift" :dialogAdd.sync="dialogAdd" @send="getSchedules"></add-edit-workshift> <!--@send, alternate to v-on:send-->
        </v-dialog>
       
        <!-- status messaging component -->
        <status-messaging :page_query_id="pageQueryId" @refresh="updateHandler"></status-messaging>
    </v-layout>  
</template>

<script>

import workShiftsApi from '@/api/workshift'
import pageKeys from '@/common/pagekeys'
import notificationTypes from '@/common/notificationTypes'
import rowsPerPageItems from '@/constants/rows-per-page'
import { hasAddRights, hasEditRights } from '@/helpers/access-permissions'
import { getSort, getRowsPerPage, getSearchKeyword, updateRowsPerPage, updateSort, updateSearchKeyword } from '@/helpers/user-prefs'

export default {    
    name: 'CompanyWorkshifts',
    metaInfo: {
        title: 'Company Workshifts',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'Company Workshifts' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
            ]
    },
    components: {
        'help-link': () => import('@/components/Common/HelpLink'),
        'add-edit-workshift': () => import('@/components/WorkShifts/AddEditWorkShifts'),
        'status-messaging': () => import('@/components/Common/StatusMessaging'),
        'update-history': () => import('@/components/Common/UpdatesHistory'),
        'comments-history': () => import('@/components/Common/CommentsHistory')
    },
    data () {      
        const routeName = this.$route.name
        const savedSort = getSort(routeName)

        return {
            rowsPerPageItems,
            paginationSync: {
                rowsPerPage: getRowsPerPage(routeName),
                sortBy: savedSort.sortBy,
                descending: savedSort.descending,
            },
            search: getSearchKeyword(routeName),
            mode: null,
            schedules: [],      
            loading: false,
            dialogAdd: false,
            error: false,
            errorMessage: '', 
            snackbarText: '',
            headers: [
                {text:'Name', align:'left', value:'name', width: '20%', sortable:true},
                {text:'Clock-Start', align:'left', value:'clockStartHr',width: '10%', sortable:true},
                {text:'Clock-End', align:'left', value: 'clockEndHr', width: '10%', sortable:true},
                {text:'Type', align:'left', value: 'strict', width:'10%', sortable:true},
                {text:'Validate Undertime', align:'left', value: 'validateUndertime', width:'10%', sortable:true},
                {text:'Grace Period', align: 'left', value:'gracePeriod', width: '10%', sortable:false},
                {text:'Total Assigned', align: 'left', value:'totalAssigned', width: '10%', sortable:true},
            ],
        }
    },
    methods: {   
        showAddEdit(data) {
            if (this.hasEditRights) {
                this.$refs.addEditWorkshift.viewWorkshiftForEdit(data)
                this.dialogAdd = true
            }
        },
        updateHandler(data) {
            if (data!=this.$store.state.userId) {
                this.getSchedules(false)
            }
            //invoke updateHistoryRef appendData 
            this.$refs.updateHistoryRef.getHistory()
        },  
        viewCompanySettings() {
            this.$router.push('/company/settings')
        }, 
        addWorkshift() {
            this.$refs.addEditWorkshift.viewWorkshiftForAdd()
            this.dialogAdd = true
        }, 
        async getSchedules(show_loading) {        
            this.dialogAdd = false
            try{
                this.loading = show_loading
                const response = await workShiftsApi.get()
                if (response && response.status == 200) {
                    this.schedules = response.data.items.map(item => ({
                        ...item,
                        clockStartHr: this.$moment(item.clockStart).format('HH:mm'),
                        clockEndHr: this.$moment(item.clockEnd).format('HH:mm')
                    }));
                }
                this.loading = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },     
    },
    created() {
        //update header titles
        this.$root.$emit('setHeaderTitles', {
            display: true,
            headerTitle: '',
            headerPageTitle: 'Workshifts'
        })
        
        this.getSchedules(true)
    },    
    watch: {
        'paginationSync.rowsPerPage'(newVal) {
            updateRowsPerPage(this.$route.name, newVal)
        },
        'paginationSync.sortBy'(newVal) {
            updateSort(this.$route.name, newVal, this.paginationSync.descending)
        },
        'paginationSync.descending'(newVal) {
            updateSort(this.$route.name, this.paginationSync.sortBy, newVal)
        },
        search(newVal) {
            updateSearchKeyword(this.$route.name, newVal)
        }
    },
    computed: {
        companyIdStr() {
            return this.$store.state.companyId
        },
        hasAddRights() {
            return hasAddRights(this.$router.currentRoute.path)
        },
        hasEditRights() {
            return hasEditRights(this.$router.currentRoute.path)
        },
        schedules_filtered() {
            var temp = this.schedules

            //search handler
            if (this.search) {
                temp = temp.filter(item => {
                    return item.name.toLowerCase().includes(this.search.toLowerCase()) ||
                            this.$moment(item.clockStart).format('h:mm a').toLowerCase().includes(this.search.toLowerCase()) ||
                            this.$moment(item.clockEnd).format('h:mm a').toLowerCase().includes(this.search.toLowerCase())
                })
            }
            return temp //.reverse()
        },
        pageQueryId() {
            return `${this.companyIdStr}_company_workshifts`
        },
        commentsData() {
            if (!this.loading) {
                return {
                    pageQueryId: this.pageQueryId,
                    pageItemCreatedBy: null,
                    pageLink: this.$router.currentRoute.path,
                    pageKey: pageKeys.workshifts,
                    notificationTypeId: notificationTypes.workshifts
                }
            }
        },
    }
}
</script>
<style scoped>
.section-layout {
    padding:0px 20px 0px 20px;
}
</style>